import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../../components/elements/button';
import '../../assets/css/pages/doctor.css';
import { doctorApi } from '../../network/apis/doctors';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

const Doctor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);

    // Doctor data state
    const [doctor, setDoctor] = useState({
        id: 1,
        name: "Dr. Sarah Johnson",
        image: "/doctors/default.jpg",
        city: "Unknown",
        state: "",
        specialization: "Aquatic Veterinarian",
        experience: 0,
        clinicName: "",
        clinicAddress: "",
        workingHours: "",
        consultationFee: "",
        qualificationCount: 0,
        certificationCount: 0,
        isVerified: false
    });

    useEffect(() => {
        const fetchDoctorData = async (doctorId) => {
            setIsLoading(true);
            try {
                const response = await doctorApi.getDoctorById(doctorId);
                console.log("Doctor Data:", response.data);
                return response.data;
            } catch (error) {
                console.error("Error fetching doctor data:", error);
                return null;
            }
        }

        fetchDoctorData(id)
            .then(data => { 
                if (data) {
                    const formatTime = (time) => {
                        if (!time) return '';
                        const [hours, minutes] = time.split(':');
                        const hour = parseInt(hours);
                        const ampm = hour >= 12 ? 'PM' : 'AM';
                        const formattedHour = hour % 12 || 12;
                        return `${formattedHour}:${minutes} ${ampm}`;
                    };

                    setDoctor({
                        id: data.id,
                        name: `Dr. ${data.user_details.first_name} ${data.user_details.last_name}`,
                        image: data.profile_image || "/doctors/default.jpg",
                        city: data.city || data.state || "Location not specified",
                        state: data.state || "",
                        specialization: data.specialization || "Aquatic Veterinarian",
                        experience: data.experience || 0,
                        clinicName: data.clinic_name || "",
                        clinicAddress: data.clinic_address || "",
                        workingHours: data.working_hours_from && data.working_hours_to 
                            ? `${formatTime(data.working_hours_from)} - ${formatTime(data.working_hours_to)}`
                            : "Not specified",
                        consultationFee: data.consultation_fee 
                            ? `₦${parseFloat(data.consultation_fee).toLocaleString()}`
                            : "Contact for pricing",
                        qualificationCount: data.qualification_count || 0,
                        certificationCount: data.certification_count || 0,
                        isVerified: data.is_verified || false
                    });
                } else {
                    console.error("No doctor data found for ID:", id);
                }
            })
            .catch(error => {   
                console.error("Error in fetching doctor data:", error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [id]);

    const handleStartChat = () => {
        // Navigate to chat with this doctor
        navigate(`/chat?doctor=${doctor.id}`);
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="doctor-profile">
            <div className="profile-header">
                <div className="profile-image">
                    <img src={doctor.image} alt={doctor.name} />
                    {doctor.isVerified && (
                        <span className="verified-badge" title="Verified Doctor">✓</span>
                    )}
                </div>
                <div className="profile-info">
                    <div className="name-row">
                        <h1>{doctor.name}</h1>
                        {doctor.isVerified && (
                            <span className="verified-tag">Verified</span>
                        )}
                    </div>
                    <p className="specialization">{doctor.specialization}</p>
                    <p className="location">
                        📍 {doctor.city}{doctor.state && `, ${doctor.state}`}
                    </p>
                    
                    <Button 
                        text="💬 Chat with Doctor"
                        variant="primary"
                        onClick={handleStartChat}
                    />
                </div>
            </div>

            <div className="profile-content">
                {/* Quick Stats */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <span className="stat-value">{doctor.experience}</span>
                        <span className="stat-label">Years Experience</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value">{doctor.qualificationCount}</span>
                        <span className="stat-label">Qualifications</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value">{doctor.certificationCount}</span>
                        <span className="stat-label">Certifications</span>
                    </div>
                </div>

                {/* Clinic Information */}
                {doctor.clinicName && (
                    <div className="profile-section">
                        <h2>🏥 Clinic Information</h2>
                        <div className="clinic-info">
                            <div className="info-row">
                                <span className="info-label">Clinic Name</span>
                                <span className="info-value">{doctor.clinicName}</span>
                            </div>
                            {doctor.clinicAddress && (
                                <div className="info-row">
                                    <span className="info-label">Address</span>
                                    <span className="info-value">{doctor.clinicAddress}</span>
                                </div>
                            )}
                            <div className="info-row">
                                <span className="info-label">Working Hours</span>
                                <span className="info-value">{doctor.workingHours}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Consultation Fee */}
                <div className="profile-section consultation-section">
                    <h2>💰 Consultation Fee</h2>
                    <div className="fee-display">
                        <span className="fee-amount">{doctor.consultationFee}</span>
                        <span className="fee-note">per consultation</span>
                    </div>
                </div>

                {/* Call to Action */}
                <div className="cta-section">
                    <h3>Have a question about your fish farm?</h3>
                    <p>Start a conversation with {doctor.name} to discuss your aquaculture health concerns.</p>
                    <Button 
                        text="Start Conversation"
                        variant="primary"
                        onClick={handleStartChat}
                    />
                </div>
            </div>
        </div>
    );
};

export default Doctor;